/**
 * Shared Hub page-shell primitives — use across Tenant Hub, Platform Admin, Client Admin.
 * Visual language: deep slate ink + teal accent (Buntu enroll brand), soft mesh canvas.
 */
import React from 'react';

export type HubTabItem<T extends string = string> = {
  id: T;
  label: string;
  icon?: React.ReactNode;
  badge?: string | number;
  disabled?: boolean;
};

type HubPageProps = {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  /** Hide the in-page title when the app bar already shows it */
  hideTitle?: boolean;
  eyebrow?: string;
  children: React.ReactNode;
  className?: string;
};

export function HubPage({
  title,
  subtitle,
  icon,
  action,
  hideTitle,
  eyebrow,
  children,
  className = '',
}: HubPageProps) {
  return (
    <div className={`hub-page ${className}`.trim()}>
      <header className="hub-page__masthead">
        <div className="hub-page__masthead-main">
          {eyebrow && <p className="hub-page__eyebrow">{eyebrow}</p>}
          {!hideTitle && (
            <h1 className="hub-page__title">
              {icon && <span className="hub-page__title-icon" aria-hidden>{icon}</span>}
              <span>{title}</span>
            </h1>
          )}
          {subtitle && <p className="hub-page__subtitle">{subtitle}</p>}
        </div>
        {action && <div className="hub-page__actions">{action}</div>}
      </header>
      <div className="hub-page__body">{children}</div>
    </div>
  );
}

type HubTabsProps<T extends string> = {
  items: HubTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** `rail` = underlined strip; `segment` = segmented control */
  variant?: 'rail' | 'segment';
  className?: string;
};

export function HubTabs<T extends string>({
  items,
  value,
  onChange,
  variant = 'rail',
  className = '',
}: HubTabsProps<T>) {
  return (
    <div
      className={`hub-tabs hub-tabs--${variant} ${className}`.trim()}
      role="tablist"
      aria-orientation="horizontal"
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            className={`hub-tabs__tab${active ? ' is-active' : ''}`}
            onClick={() => onChange(item.id)}
          >
            {item.icon && <span className="hub-tabs__icon">{item.icon}</span>}
            <span>{item.label}</span>
            {item.badge != null && item.badge !== '' && (
              <span className="hub-tabs__badge">{item.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

type HubPanelProps = {
  children: React.ReactNode;
  /** When true, wraps children in the standard white panel */
  padded?: boolean;
  className?: string;
  /** Flush tabs against panel top (tabs sit above children inside panel) */
  withTabs?: boolean;
};

export function HubPanel({ children, padded = true, className = '', withTabs }: HubPanelProps) {
  return (
    <section
      className={`hub-panel${withTabs ? ' hub-panel--with-tabs' : ''} ${className}`.trim()}
    >
      <div className={padded ? 'hub-panel__inner' : undefined}>{children}</div>
    </section>
  );
}

type HubEmptyProps = {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
};

export function HubEmpty({ title, description, action, icon }: HubEmptyProps) {
  return (
    <div className="hub-empty">
      {icon && <div className="hub-empty__icon">{icon}</div>}
      <h3 className="hub-empty__title">{title}</h3>
      {description && <p className="hub-empty__desc">{description}</p>}
      {action && <div className="hub-empty__action">{action}</div>}
    </div>
  );
}

/** Convenience: page + optional tab rail + panel content */
type HubSectionProps<T extends string> = {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  hideTitle?: boolean;
  eyebrow?: string;
  tabs?: HubTabItem<T>[];
  tab?: T;
  onTabChange?: (id: T) => void;
  tabVariant?: 'rail' | 'segment';
  children: React.ReactNode;
};

export function HubSection<T extends string>({
  title,
  subtitle,
  icon,
  action,
  hideTitle,
  eyebrow,
  tabs,
  tab,
  onTabChange,
  tabVariant = 'rail',
  children,
}: HubSectionProps<T>) {
  return (
    <HubPage
      title={title}
      subtitle={subtitle}
      icon={icon}
      action={action}
      hideTitle={hideTitle}
      eyebrow={eyebrow}
    >
      <HubPanel withTabs={!!tabs} padded={!tabs}>
        {tabs && tab != null && onTabChange && (
          <HubTabs items={tabs} value={tab} onChange={onTabChange} variant={tabVariant} />
        )}
        <div className={tabs ? 'hub-panel__inner' : undefined}>{children}</div>
      </HubPanel>
    </HubPage>
  );
}
